import { useEffect, useState } from "react";
import { Routes, Route, Navigate, NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { LogOut, ShieldAlert, Menu, X, ChevronDown } from "lucide-react";
import { getToken, clearSession } from "./api";
import { MODULES, PERSONAL_EXTRA, useAccess } from "./permissions";
import BrandLogo from "../components/BrandLogo";
import ModulePending from "./components/ModulePending";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Attendance from "./pages/Attendance";
import Leave from "./pages/Leave";
import Holidays from "./pages/Holidays";
import Profile from "./pages/Profile";
import AdminHome from "./admin/AdminHome";
import AttendanceAdmin from "./admin/AttendanceAdmin";
import LeavesAdmin from "./admin/LeavesAdmin";
import HolidaysAdmin from "./admin/HolidaysAdmin";
import Branches from "./admin/Branches";
import Reports from "./admin/Reports";
import Users from "./admin/Users";
import LoginManagement from "./admin/LoginManagement";
import Roles from "./admin/Roles";
import Permissions from "./admin/Permissions";
import AuditLogs from "./admin/AuditLogs";
import MasterDataHub from "./admin/MasterDataHub";
import EmployeeMaster from "./hrms/EmployeeMaster";
import LiveMonitor from "./attendance/LiveMonitor";
import Properties from "./admin/Properties";
import Departments from "./hrms/Departments";
import MasterData from "./hrms/MasterData";
import {
  UserPlus, FileBadge, HardHat, ShoppingCart, Truck, Warehouse, Laptop, LifeBuoy,
  Stamp, FolderOpen, Settings,
} from "lucide-react";

const RequireAuth = ({ children }) => (getToken() ? children : <Navigate to="/app/login" replace />);

const AccessDenied = () => (
  <div className="max-w-md mx-auto bg-white border border-slate-200 p-8 text-center" data-testid="admin-access-denied">
    <ShieldAlert size={28} className="mx-auto text-[#C5221F]" />
    <h1 className="mt-4 font-display text-lg font-bold text-slate-900">No access to this module</h1>
    <p className="mt-2 text-sm text-slate-500">Your GR CONNECT permissions do not include this area. Contact your administrator if you believe this is wrong.</p>
    <Link to="/app" className="mt-5 inline-block text-xs font-semibold text-[#C5221F] hover:text-white hover:underline" data-testid="access-denied-back">
      Back to Dashboard
    </Link>
  </div>
);

const RequireModule = ({ moduleKey, children }) => {
  const access = useAccess();
  if (!access.can(moduleKey)) return <AccessDenied />;
  return children;
};

// Roadmap modules rendered as professional placeholders (no fake data, no second database).
const PENDING_META = {
  "recruitment": { icon: UserPlus, title: "Recruitment", blurb: "Job openings, candidates and hiring pipelines will run here once the central HR backend enables the recruitment module." },
  "employee-documents": { icon: FileBadge, title: "Employee Documents", blurb: "Offer letters, contracts and employee document records will be managed here in a later phase." },
  "projects": { icon: HardHat, title: "Projects & Sites", blurb: "Project and site tracking will connect GR CONNECT to live project data in a later phase." },
  "purchase": { icon: ShoppingCart, title: "Purchase", blurb: "Purchase requisitions, orders and procurement workflows arrive in a later phase." },
  "vendors": { icon: Truck, title: "Vendors", blurb: "The vendor master and supplier management will be activated in a later phase." },
  "stores": { icon: Warehouse, title: "Stores & Inventory", blurb: "Stock, material issue and inventory control will be activated in a later phase." },
  "assets": { icon: Laptop, title: "Assets", blurb: "Company asset allocation and tracking will be activated in a later phase." },
  "helpdesk": { icon: LifeBuoy, title: "IT Helpdesk", blurb: "Internal IT support tickets will be managed here in a later phase." },
  "approvals": { icon: Stamp, title: "Approvals", blurb: "A unified approval inbox across modules will be activated in a later phase." },
  "documents": { icon: FolderOpen, title: "Documents", blurb: "Central company document management will be activated in a later phase." },
  "settings": { icon: Settings, title: "Settings", blurb: "Portal configuration will be available here in a later phase." },
};

const Pending = ({ kind }) => <ModulePending {...(PENDING_META[kind] || {})} />;

// ---------- sidebar ----------
const NavItem = ({ mod, onNavigate }) => (
  <NavLink
    to={mod.to}
    end={mod.end}
    onClick={onNavigate}
    data-testid={`grc-nav-${mod.key.replace(/[.]/g, "-")}`}
    className={({ isActive }) =>
      `flex items-center gap-2.5 px-3 py-2 text-[12px] font-display font-semibold tracking-wide border-l-2 transition-colors duration-150 ${
        isActive ? "border-[#E8574F] text-white bg-white/5" : "border-transparent text-slate-400 hover:text-white hover:bg-white/5"
      }`
    }
  >
    <mod.icon size={14} className="shrink-0" />
    <span className="flex-1 truncate">{mod.label}</span>
    {mod.pending && (
      <span className="shrink-0 text-[8px] font-bold uppercase tracking-widest text-amber-400/90 border border-amber-400/30 px-1.5 py-0.5">Soon</span>
    )}
  </NavLink>
);

const NavGroup = ({ label, items, onNavigate, testid }) => {
  const location = useLocation();
  const containsActive = items.some((m) => (m.end ? location.pathname === m.to : location.pathname.startsWith(m.to)));
  const [open, setOpen] = useState(true);
  useEffect(() => { if (containsActive) setOpen(true); }, [containsActive]);
  return (
    <div data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between px-3 pt-5 pb-1.5 text-[9px] font-mono-tech font-bold uppercase tracking-[0.3em] text-slate-500 hover:text-slate-300 transition-colors"
      >
        {label}
        <ChevronDown size={12} className={`transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="space-y-0.5">
          {items.map((m) => <NavItem key={m.key} mod={m} onNavigate={onNavigate} />)}
        </div>
      )}
    </div>
  );
};

const SidebarNav = ({ onNavigate }) => {
  const access = useAccess();
  const visible = MODULES.filter((m) => access.can(m.key));
  const personalExtras = PERSONAL_EXTRA.filter((m) => access.can(m.key));
  const bySection = (s) => visible.filter((m) => m.section === s);
  // Spec order: Dashboard → HRMS → Attendance → ERP placeholders → Reports → Admin → Profile
  const ordered = [
    { label: "HRMS", testid: "grc-group-hrms", items: bySection("HRMS") },
    { label: "Attendance", testid: "grc-group-attendance", items: bySection("Attendance") },
    { flat: visible.filter((m) => !m.section && !m.personal) },
    { label: "Admin", testid: "grc-group-admin", items: bySection("Admin") },
  ];
  const dashboard = visible.find((m) => m.key === "dashboard");
  const profile = visible.find((m) => m.key === "profile");
  // Ordinary employees keep their personal pages (My Leave, Holidays) as flat entries.
  const personalFlat = access.adminArea ? [] : personalExtras;

  return (
    <nav className="flex-1 overflow-y-auto px-3 pb-6" data-testid="grc-sidebar-nav">
      <div className="pt-4 space-y-0.5">
        {dashboard && <NavItem mod={dashboard} onNavigate={onNavigate} />}
        {personalFlat.map((m) => <NavItem key={m.key} mod={m} onNavigate={onNavigate} />)}
      </div>
      {ordered.map((entry, i) =>
        entry.flat ? (
          entry.flat.length > 0 && (
            <div key="flat" className="pt-4 space-y-0.5" data-testid="grc-flat-modules">
              {entry.flat.map((m) => <NavItem key={m.key} mod={m} onNavigate={onNavigate} />)}
            </div>
          )
        ) : (
          entry.items.length > 0 && <NavGroup key={entry.label} {...entry} onNavigate={onNavigate} />
        )
      )}
      {profile && (
        <div className="pt-4 mt-4 border-t border-white/10">
          <NavItem mod={profile} onNavigate={onNavigate} />
        </div>
      )}
    </nav>
  );
};

const SidebarBrand = () => (
  <div className="px-5 py-5 border-b border-white/10">
    <div className="flex items-center gap-3">
      <BrandLogo compact />
      <span className="leading-none">
        <span className="block font-display font-extrabold tracking-tight text-white text-sm">GR CONNECT</span>
        <span className="block font-mono-tech text-[8px] tracking-[0.3em] text-slate-500 uppercase mt-1">HRMS · ERP Portal</span>
      </span>
    </div>
  </div>
);

// ---------- shell ----------
const Shell = ({ children }) => {
  const navigate = useNavigate();
  const access = useAccess();
  const user = access.user;
  const [drawer, setDrawer] = useState(false);
  const logout = () => {
    clearSession();
    navigate("/app/login");
  };
  return (
    <div className="min-h-screen bg-slate-100" data-testid="staff-shell">
      {/* desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-[#0A1128]" data-testid="grc-sidebar">
        <SidebarBrand />
        <SidebarNav />
      </aside>

      {/* mobile drawer */}
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="grc-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawer(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] flex flex-col bg-[#0A1128]">
            <div className="flex items-center justify-between pr-3">
              <SidebarBrand />
              <button onClick={() => setDrawer(false)} data-testid="grc-drawer-close" className="p-2 text-slate-400 hover:text-white"><X size={18} /></button>
            </div>
            <SidebarNav onNavigate={() => setDrawer(false)} />
          </aside>
        </div>
      )}

      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* top bar */}
        <header className="sticky top-0 z-40 bg-white border-b border-slate-200" data-testid="grc-topbar">
          <div className="flex items-center justify-between gap-4 px-4 sm:px-6 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <button onClick={() => setDrawer(true)} data-testid="grc-mobile-menu-btn" className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900">
                <Menu size={18} />
              </button>
              <span className="lg:hidden font-display font-extrabold tracking-tight text-[#0A1128] text-sm">GR CONNECT</span>
              {access.superadmin && (
                <span className="hidden sm:inline-block border border-red-200 bg-red-50 text-[#C5221F] px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em]" data-testid="grc-superadmin-badge">
                  SuperAdmin
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-xs text-slate-600 truncate max-w-[220px]" data-testid="staff-user-name">
                {user?.name || ""}{user?.role ? <span className="text-slate-400"> · {user.role}</span> : null}
              </span>
              <button
                onClick={logout}
                data-testid="staff-logout-btn"
                className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-[#C5221F] hover:text-[#C5221F] px-3.5 py-1.5 text-[10px] font-display font-semibold uppercase tracking-widest text-slate-600 transition-colors duration-200"
              >
                <LogOut size={11} /> Logout
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-6 py-8 max-w-[1400px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
};

// Admin overview requires any privileged area access (preserves old Admin-only page).
const AdminGate = ({ children }) => {
  const access = useAccess();
  if (!access.adminArea) return <AccessDenied />;
  return children;
};

// Placeholder route resolver for /app/modules/:kind — unknown kinds bounce to dashboard.
const PendingRoute = () => {
  const { pathname } = useLocation();
  const kind = pathname.split("/").pop();
  const mod = MODULES.find((m) => m.to === `/app/modules/${kind}`);
  if (!mod) return <Navigate to="/app" replace />;
  return (
    <RequireModule moduleKey={mod.key}>
      <Pending kind={kind} />
    </RequireModule>
  );
};

const StaffApp = () => {
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    const prev = meta ? meta.getAttribute("content") : null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex, nofollow");
    return () => {
      if (prev) meta.setAttribute("content", prev);
      else meta.remove();
    };
  }, []);

  const P = ({ moduleKey, children }) => (
    <RequireAuth><Shell><RequireModule moduleKey={moduleKey}>{children}</RequireModule></Shell></RequireAuth>
  );

  return (
    <Routes>
      <Route path="login" element={<Login />} />

      {/* personal */}
      <Route index element={<P moduleKey="dashboard"><Dashboard /></P>} />
      <Route path="attendance" element={<P moduleKey="attendance.mine"><Attendance /></P>} />
      <Route path="leave" element={<P moduleKey="leave.mine"><Leave /></P>} />
      <Route path="holidays" element={<P moduleKey="holidays.view"><Holidays /></P>} />
      <Route path="profile" element={<P moduleKey="profile"><Profile /></P>} />

      {/* HRMS */}
      <Route path="hrms/employees" element={<P moduleKey="hrms.employees"><EmployeeMaster /></P>} />
      <Route path="hrms/departments" element={<P moduleKey="hrms.departments"><Departments /></P>} />
      <Route path="hrms/designations" element={<P moduleKey="hrms.designations"><MasterData kind="designations" /></P>} />
      <Route path="hrms/grades" element={<P moduleKey="hrms.grades"><MasterData kind="grades" /></P>} />
      <Route path="hrms/shifts" element={<P moduleKey="hrms.shifts"><MasterData kind="shifts" /></P>} />
      <Route path="hrms/leaves" element={<P moduleKey="hrms.leaves"><LeavesAdmin /></P>} />
      <Route path="hrms/holidays" element={<P moduleKey="hrms.holidays"><HolidaysAdmin /></P>} />
      <Route path="hrms/recruitment" element={<P moduleKey="hrms.recruitment"><Pending kind="recruitment" /></P>} />
      <Route path="hrms/documents" element={<P moduleKey="hrms.documents"><Pending kind="employee-documents" /></P>} />

      {/* attendance */}
      <Route path="attendance/live" element={<P moduleKey="attendance.live"><LiveMonitor /></P>} />
      <Route path="attendance/all" element={<P moduleKey="attendance.all"><AttendanceAdmin /></P>} />
      <Route path="reports" element={<P moduleKey="reports"><Reports /></P>} />

      {/* ERP roadmap placeholders */}
      <Route path="modules/:kind" element={<RequireAuth><Shell><PendingRoute /></Shell></RequireAuth>} />

      {/* admin */}
      <Route path="admin" element={<RequireAuth><Shell><AdminGate><AdminHome /></AdminGate></Shell></RequireAuth>} />
      <Route path="admin/users" element={<P moduleKey="admin.users"><Users /></P>} />
      <Route path="admin/login-management" element={<P moduleKey="admin.login_management"><LoginManagement /></P>} />
      <Route path="admin/roles" element={<P moduleKey="admin.roles"><Roles /></P>} />
      <Route path="admin/permissions" element={<P moduleKey="admin.permissions"><Permissions /></P>} />
      <Route path="admin/master-data" element={<P moduleKey="admin.master_data"><MasterDataHub /></P>} />
      <Route path="admin/audit-logs" element={<P moduleKey="admin.audit_logs"><AuditLogs /></P>} />
      <Route path="admin/settings" element={<P moduleKey="admin.settings"><Pending kind="settings" /></P>} />
      <Route path="admin/branches" element={<P moduleKey="admin.master_data"><Branches /></P>} />
      <Route path="admin/properties" element={<P moduleKey="admin.properties"><Properties /></P>} />

      {/* legacy admin URLs — kept working via redirect */}
      <Route path="admin/employees" element={<Navigate to="/app/hrms/employees" replace />} />
      <Route path="admin/attendance" element={<Navigate to="/app/attendance/all" replace />} />
      <Route path="admin/leaves" element={<Navigate to="/app/hrms/leaves" replace />} />
      <Route path="admin/holidays" element={<Navigate to="/app/hrms/holidays" replace />} />
      <Route path="admin/reports" element={<Navigate to="/app/reports" replace />} />

      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
};

export default StaffApp;
